import { useQuery } from '@tanstack/react-query';
import { issuer_raw } from '@ui/assets/config/issuer-raw';
import { LinkExternal } from '@ui/components/icons';
import { TextTooltip } from '@ui/components/TextTooltip';
import { Interval } from '@ui/constants/poll';
import { useDataChainId } from '@ui/hooks/datachain';
import { EPOCH_DURATION_MS,useMonadEpoch } from '@ui/hooks/useMonadEpoch';
import { formatBigValue } from '@ui/lib/bignumber';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import ISSUER_ABI from '@ui/services/contract/abis/Issuer';
import MON_STAKING_VAULT_ABI from '@ui/services/contract/abis/MONStakingVault';
import { getTokenAddress } from '@ui/services/contract/constants/address';
import { formatDateUtc } from '@ui/utils/formatDateUtc';
import { getExplorerLink } from '@ui/utils/links';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { Box, Flex, HStack, VStack } from 'styled-system/jsx';
import { parseAbi, parseAbiItem } from 'viem';
import { usePublicClient, useReadContract } from 'wagmi';

const MONAD_STAKING_ADDRESS = '0x0000000000000000000000000000000000001000';
const MONAD_STAKING_READ_ABI = parseAbi([
  'function getDelegations(address delegator, uint64 startValId) returns (bool isDone, uint64 nextValId, uint64[] valIds)',
  'function getDelegator(uint64 validatorId, address delegator) returns (uint256 stake, uint256 accRewardPerToken, uint256 unclaimedRewards, uint256 deltaStake, uint256 nextDeltaStake, uint64 deltaEpoch, uint64 nextDeltaEpoch)',
]);
const INTEREST_DISTRIBUTED_EVENT = parseAbiItem(
  'event InterestDistributed(uint256 interest, uint256 interestFee, uint256 interestReceiverAmount)',
);
const INTEREST_KEEPER_ADDRESS =
  '0xBDF8292bc05c0380C273A04Ec7cd799FBa3cBD34' as const;
const LOG_CHUNK_SIZE = 100n; // RPC max range constraint
const LOG_BATCH_SIZE = 12;
const MAX_FALLBACK_SEARCH_BLOCKS = 1_000_000n;
const KEEPER_LOOKBACK_BUFFER_BLOCKS = 500n;
const KEEPER_BINARY_SEARCH_RANGE_BLOCKS = 51_000n;
const LOG_LOOKBACK_BUFFER_BLOCKS = 100n;

type LastInterestDistributionData = {
  timestampMs: number;
  interest: bigint;
  interestReceiverAmount: bigint;
  transactionHash: string | null;
};

type LastInterestDistributionCache = {
  keeperNonce: number;
  keeperLastTxBlock: bigint | null;
  lastLogBlock: bigint;
  data: LastInterestDistributionData;
};

const lastInterestDistributionCacheByIssuer = new Map<
  string,
  LastInterestDistributionCache
>();

/**
 * LVMON issuer + treasury vault lookup. Exported so the page footer can link
 * to the vault's validator set; wagmi dedupes the underlying reads by query
 * key so sharing this hook costs no extra RPC.
 */
export const useLvmonTreasuryVault = () => {
  const chainId = useDataChainId();
  const wmonAddress = getTokenAddress('WMON', chainId);

  const issuerAddress = issuer_raw[chainId]?.find(
    (config) => config.lvTokenSymbol === 'LVMON',
  )?.issuerAddress;

  const { data: reserveConfig } = useReadContract({
    address: issuerAddress,
    abi: ISSUER_ABI,
    functionName: 'reserveTokenConfig',
    args: wmonAddress ? [wmonAddress] : undefined,
    query: { enabled: !!issuerAddress && !!wmonAddress },
  });

  return {
    issuerAddress,
    treasuryVaultAddress: reserveConfig?.treasuryVault,
  };
};

/** V2 rail card shell — panel bg, hairline rows inside. */
const railCardStyle = {
  direction: 'column',
  gap: '12px',
  p: '16px',
  bg: 'v2.bg.panel',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'v2.border.default',
  borderRadius: '4px',
} as const;

const InfoRow = (props: {
  label: React.ReactNode;
  isFirst?: boolean;
  children: React.ReactNode;
}) => {
  const { label, isFirst, children } = props;

  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      gap="8px"
      minH="28px"
      py="2px"
      borderTopWidth={isFirst ? '0px' : '1px'}
      borderStyle="solid"
      borderColor="v2.border.hairline"
      flexWrap="wrap"
      rowGap="2px"
    >
      <Text fontSize="12px" color="v2.text.muted" whiteSpace="nowrap">
        {label}
      </Text>
      <HStack gap="6px" alignItems="center" minW={0}>
        {children}
      </HStack>
    </HStack>
  );
};

const AssetRow = (props: {
  dotColor: string;
  name: string;
  isFirst?: boolean;
  children: React.ReactNode;
}) => {
  const { dotColor, name, isFirst, children } = props;

  return (
    <HStack
      gap="8px"
      alignItems="center"
      minH="30px"
      borderTopWidth={isFirst ? '0px' : '1px'}
      borderStyle="solid"
      borderColor="v2.border.hairline"
    >
      {/* consistent 6px dot on every row: neutral when zero, colored when active */}
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg={dotColor}
        flexShrink={0}
      />
      <Text fontSize="12px" color="v2.text.muted">
        {name}
      </Text>
      <HStack gap="6px" alignItems="center" ml="auto">
        {children}
      </HStack>
    </HStack>
  );
};

export const StakeProtocolAssetsV3 = () => {
  const { t } = useTranslation();
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { data: epochInfo, isPending: isEpochInfoPending } = useMonadEpoch();

  const { issuerAddress, treasuryVaultAddress } = useLvmonTreasuryVault();

  const { data: stakingState } = useReadContract({
    address: treasuryVaultAddress,
    abi: MON_STAKING_VAULT_ABI,
    functionName: 'stakingState',
    query: {
      enabled: !!treasuryVaultAddress,
      refetchInterval: Interval.SLOW,
    },
  });

  // stakingState is [stakedBalance, lastEpoch]
  // @ts-ignore
  const stakedBalance = stakingState?.[0] || stakingState?.stakedBalance;

  const {
    data: lastInterestDistribution,
    isPending: isLastInterestDistributionPending,
  } = useQuery({
    queryKey: ['lvmon-last-interest-distribution', chainId, issuerAddress],
    queryFn: async () => {
      if (!publicClient || !issuerAddress) return null;

      const cacheKey = `${chainId}:${issuerAddress.toLowerCase()}`;
      const cached = lastInterestDistributionCacheByIssuer.get(cacheKey);
      const currentBlockNumber = await publicClient.getBlockNumber();
      const keeperNonce = await publicClient.getTransactionCount({
        address: INTEREST_KEEPER_ADDRESS,
      });

      if (cached && cached.keeperNonce === keeperNonce) {
        return cached.data;
      }

      const findKeeperLastTxBlock = async (): Promise<bigint | null> => {
        if (keeperNonce === 0) return null;

        const runBinarySearch = async (leftBoundary: bigint) => {
          let left = leftBoundary;
          let right = currentBlockNumber;
          let answer: bigint | null = null;

          while (left <= right) {
            const mid = (left + right) / 2n;
            const nonceAtMid = await publicClient.getTransactionCount({
              address: INTEREST_KEEPER_ADDRESS,
              blockNumber: mid,
            });

            if (nonceAtMid >= keeperNonce) {
              answer = mid;
              if (mid === 0n) break;
              right = mid - 1n;
            } else {
              left = mid + 1n;
            }
          }

          return answer;
        };

        const boundedLeftByRange =
          currentBlockNumber > KEEPER_BINARY_SEARCH_RANGE_BLOCKS
            ? currentBlockNumber - KEEPER_BINARY_SEARCH_RANGE_BLOCKS
            : 0n;
        const boundedLeftByCache = cached?.keeperLastTxBlock ?? 0n;
        const boundedLeft =
          boundedLeftByCache > boundedLeftByRange
            ? boundedLeftByCache
            : boundedLeftByRange;

        const boundedAnswer = await runBinarySearch(boundedLeft);
        if (boundedAnswer === null) return runBinarySearch(0n);
        if (boundedAnswer > boundedLeft || boundedAnswer === 0n) {
          return boundedAnswer;
        }

        // If nonce already equals latest before boundedLeft, boundary is outside range.
        const nonceBeforeBoundedLeft = await publicClient.getTransactionCount({
          address: INTEREST_KEEPER_ADDRESS,
          blockNumber: boundedLeft - 1n,
        });
        if (nonceBeforeBoundedLeft >= keeperNonce) {
          return runBinarySearch(0n);
        }

        return boundedAnswer;
      };

      const findLatestInterestLogInRange = async (fromBlock: bigint, toBlock: bigint) => {
        if (fromBlock > toBlock) return null;

        const searchSpan = toBlock - fromBlock;
        const batchSpan = LOG_CHUNK_SIZE * BigInt(LOG_BATCH_SIZE);
        for (let baseOffset = 0n; baseOffset <= searchSpan; baseOffset += batchSpan) {
          const batchPromises = [];

          for (let i = 0; i < LOG_BATCH_SIZE; i++) {
            const offset = baseOffset + (LOG_CHUNK_SIZE * BigInt(i));
            if (offset > searchSpan) break;

            const chunkTo = toBlock > offset ? toBlock - offset : fromBlock;
            const chunkFromCandidate =
              chunkTo > LOG_CHUNK_SIZE ? chunkTo - LOG_CHUNK_SIZE : 0n;
            const chunkFrom =
              chunkFromCandidate > fromBlock ? chunkFromCandidate : fromBlock;

            batchPromises.push(
              publicClient.getLogs({
                address: issuerAddress,
                event: INTEREST_DISTRIBUTED_EVENT,
                fromBlock: chunkFrom,
                toBlock: chunkTo,
              }),
            );
          }

          const batchLogs = await Promise.all(batchPromises);
          for (const logs of batchLogs) {
            if (logs.length === 0) continue;

            const latestLog = logs[logs.length - 1];
            if (latestLog.blockNumber === null || !latestLog.args) continue;

return latestLog;
          }

          if (toBlock <= baseOffset + batchSpan) break;
        }

        return null;
      };

      const keeperLastTxBlock = await findKeeperLastTxBlock();
      const defaultSearchFrom =
        currentBlockNumber > MAX_FALLBACK_SEARCH_BLOCKS
          ? currentBlockNumber - MAX_FALLBACK_SEARCH_BLOCKS
          : 0n;
      const knownLogSearchFrom =
        cached?.lastLogBlock !== undefined
          ? cached.lastLogBlock > LOG_LOOKBACK_BUFFER_BLOCKS
            ? cached.lastLogBlock - LOG_LOOKBACK_BUFFER_BLOCKS
            : 0n
          : 0n;
      let searchFromBlock = defaultSearchFrom;
      if (knownLogSearchFrom > searchFromBlock) {
        searchFromBlock = knownLogSearchFrom;
      }

      // Fast lane: directly probe around keeper's latest tx block.
      if (keeperLastTxBlock !== null) {
        const keeperExactLog = await findLatestInterestLogInRange(
          keeperLastTxBlock,
          keeperLastTxBlock,
        );
        if (keeperExactLog) {
          const block = await publicClient.getBlock({
            blockNumber: keeperExactLog.blockNumber,
          });

          const data = {
            timestampMs: Number(block.timestamp) * 1000,
            interest: keeperExactLog.args.interest || 0n,
            interestReceiverAmount: keeperExactLog.args.interestReceiverAmount || 0n,
            transactionHash: keeperExactLog.transactionHash ?? null,
          };
          lastInterestDistributionCacheByIssuer.set(cacheKey, {
            keeperNonce,
            keeperLastTxBlock,
            lastLogBlock: keeperExactLog.blockNumber,
            data,
          });

return data;
        }

        const keeperProbeFrom =
          keeperLastTxBlock > KEEPER_LOOKBACK_BUFFER_BLOCKS
            ? keeperLastTxBlock - KEEPER_LOOKBACK_BUFFER_BLOCKS
            : 0n;
        const keeperProbeTo =
          keeperLastTxBlock + KEEPER_LOOKBACK_BUFFER_BLOCKS < currentBlockNumber
            ? keeperLastTxBlock + KEEPER_LOOKBACK_BUFFER_BLOCKS
            : currentBlockNumber;
        const keeperProbeLog = await findLatestInterestLogInRange(
          keeperProbeFrom,
          keeperProbeTo,
        );
        if (keeperProbeLog) {
          const block = await publicClient.getBlock({
            blockNumber: keeperProbeLog.blockNumber,
          });

          const data = {
            timestampMs: Number(block.timestamp) * 1000,
            interest: keeperProbeLog.args.interest || 0n,
            interestReceiverAmount: keeperProbeLog.args.interestReceiverAmount || 0n,
            transactionHash: keeperProbeLog.transactionHash ?? null,
          };
          lastInterestDistributionCacheByIssuer.set(cacheKey, {
            keeperNonce,
            keeperLastTxBlock,
            lastLogBlock: keeperProbeLog.blockNumber,
            data,
          });

return data;
        }
      }

      const fallbackLog = await findLatestInterestLogInRange(
        searchFromBlock,
        currentBlockNumber,
      );
      if (fallbackLog) {
        const block = await publicClient.getBlock({
          blockNumber: fallbackLog.blockNumber,
        });
        const data = {
          timestampMs: Number(block.timestamp) * 1000,
          interest: fallbackLog.args.interest || 0n,
          interestReceiverAmount: fallbackLog.args.interestReceiverAmount || 0n,
          transactionHash: fallbackLog.transactionHash ?? null,
        };
        lastInterestDistributionCacheByIssuer.set(cacheKey, {
          keeperNonce,
          keeperLastTxBlock,
          lastLogBlock: fallbackLog.blockNumber,
          data,
        });

return data;
      }

      if (cached) {
        lastInterestDistributionCacheByIssuer.set(cacheKey, {
          ...cached,
          keeperNonce,
          keeperLastTxBlock,
        });

return cached.data;
      }

      return null;
    },
    enabled: !!publicClient && !!issuerAddress,
    refetchInterval: Interval.SLOW,
    staleTime: Interval.SLOW,
  });

  const {
    data: nextInterestEstimate,
    isPending: isNextInterestEstimatePending,
  } = useQuery({
    queryKey: [
      'lvmon-next-interest-estimate',
      chainId,
      treasuryVaultAddress,
      lastInterestDistribution?.interest?.toString(),
      lastInterestDistribution?.interestReceiverAmount?.toString(),
    ],
    queryFn: async () => {
      if (!publicClient || !treasuryVaultAddress) return null;

      let nextValidatorId = 0n;
      let isDone = false;
      let loops = 0;
      let totalUnclaimedRewards = 0n;

      while (!isDone && loops < 20) {
        const [pageDone, pageNextValidatorId, validatorIds] =
          (await publicClient.readContract({
            address: MONAD_STAKING_ADDRESS,
            abi: MONAD_STAKING_READ_ABI,
            functionName: 'getDelegations',
            args: [treasuryVaultAddress, nextValidatorId],
          })) as unknown as [boolean, bigint, bigint[]];

        for (const validatorId of validatorIds) {
          const [, , unclaimedRewards] = (await publicClient.readContract({
            address: MONAD_STAKING_ADDRESS,
            abi: MONAD_STAKING_READ_ABI,
            functionName: 'getDelegator',
            args: [validatorId, treasuryVaultAddress],
          })) as unknown as [bigint, bigint, bigint, bigint, bigint, bigint, bigint];

          totalUnclaimedRewards += unclaimedRewards;
        }

        isDone = pageDone;
        nextValidatorId = pageNextValidatorId;
        loops += 1;
      }

      let receiverRateBps = 10_000n;
      if (
        lastInterestDistribution?.interest &&
        lastInterestDistribution.interest > 0n &&
        lastInterestDistribution.interestReceiverAmount !== undefined
      ) {
        receiverRateBps =
          (lastInterestDistribution.interestReceiverAmount * 10_000n) /
          lastInterestDistribution.interest;
      }

      return {
        totalUnclaimedRewards,
        estimatedInterestReceiverAmount:
          (totalUnclaimedRewards * receiverRateBps) / 10_000n,
      };
    },
    enabled: !!publicClient && !!treasuryVaultAddress,
    refetchInterval: Interval.SLOW,
    staleTime: Interval.SLOW,
  });

  const nextEpochUpdateTimeMs = epochInfo
    ? epochInfo.startTimeMs + EPOCH_DURATION_MS
    : null;

  const nextInterestDistributionTimeMs = useMemo(() => {
    if (!lastInterestDistribution?.timestampMs || !nextEpochUpdateTimeMs) return null;

    let nextTime = lastInterestDistribution.timestampMs + EPOCH_DURATION_MS;
    while (nextTime < nextEpochUpdateTimeMs) {
      nextTime += EPOCH_DURATION_MS;
    }

return nextTime;
  }, [lastInterestDistribution?.timestampMs, nextEpochUpdateTimeMs]);

  const formattedStakedBalance = stakedBalance
    ? formatBigValue(stakedBalance, {
      decimals: 18,
      autoKmb: true,
    })
    : '-';
  const loadingText = 'Loading...';
  const estimatingText = 'Estimating...';
  const timeFallbackText =
    isEpochInfoPending || isLastInterestDistributionPending ? loadingText : '-';
  const distributionAmountFallbackText = isLastInterestDistributionPending
    ? loadingText
    : '-';
  const estimateFallbackText =
    isEpochInfoPending || isNextInterestEstimatePending ? estimatingText : '-';
  const formattedLastInterestTime = lastInterestDistribution?.timestampMs
    ? formatDateUtc(new Date(lastInterestDistribution.timestampMs))
    : timeFallbackText;
  const formattedLastInterestAmount = lastInterestDistribution?.interestReceiverAmount
    ? formatBigValue(lastInterestDistribution.interestReceiverAmount, {
      decimals: 18,
      autoKmb: false,
    })
    : distributionAmountFallbackText;
  const formattedNextInterestTime = nextInterestDistributionTimeMs
    ? formatDateUtc(new Date(nextInterestDistributionTimeMs))
    : timeFallbackText;
  const estimatedFullCycleInterestReceiverAmount = useMemo(() => {
    const currentEstimatedAmount =
      nextInterestEstimate?.estimatedInterestReceiverAmount;
    if (currentEstimatedAmount === undefined) return null;
    if (!epochInfo?.startTimeMs) return null;

    const elapsedMs = Date.now() - epochInfo.startTimeMs;
    const boundedElapsedMs = Math.min(Math.max(1, elapsedMs), EPOCH_DURATION_MS);

    return (
      (currentEstimatedAmount * BigInt(EPOCH_DURATION_MS)) /
      BigInt(boundedElapsedMs)
    );
  }, [nextInterestEstimate?.estimatedInterestReceiverAmount, epochInfo?.startTimeMs]);
  const formattedRealtimeInterestEstimate =
    nextInterestEstimate?.estimatedInterestReceiverAmount !== undefined
      ? formatBigValue(nextInterestEstimate.estimatedInterestReceiverAmount, {
        decimals: 18,
        autoKmb: false,
      })
      : estimateFallbackText;
  const formattedFullCycleInterestEstimate =
    estimatedFullCycleInterestReceiverAmount !== null
      ? formatBigValue(estimatedFullCycleInterestReceiverAmount, {
        decimals: 18,
        autoKmb: false,
      })
      : estimateFallbackText;
  const formattedLastInterestAmountWithUnit =
    formattedLastInterestAmount === distributionAmountFallbackText
      ? distributionAmountFallbackText
      : `${formattedLastInterestAmount} LVMON`;
  const formattedRealtimeInterestEstimateWithUnit =
    formattedRealtimeInterestEstimate === estimateFallbackText
      ? estimateFallbackText
      : `${formattedRealtimeInterestEstimate} LVMON`;
  const formattedFullCycleInterestEstimateWithUnit =
    formattedFullCycleInterestEstimate === estimateFallbackText
      ? estimateFallbackText
      : `${formattedFullCycleInterestEstimate} LVMON`;
  const formattedLastDistributionDisplay =
    formattedLastInterestTime === loadingText ||
    formattedLastInterestAmountWithUnit === loadingText
      ? loadingText
      : formattedLastInterestTime === '-' ||
          formattedLastInterestAmountWithUnit === '-'
        ? '-'
      : `${formattedLastInterestTime} (${formattedLastInterestAmountWithUnit})`;

  const explorerIconLink = (href: string, tooltip: string) => (
    <TextTooltip
      content={tooltip}
      triggerCss={{ textDecoration: 'none' }}
      contentCss={{ p: '4px 6px' }}
    >
      <Icon
        onClick={() => {
          window.open(href, '_blank');
        }}
        cursor="pointer"
        boxSize="11px"
        color="v2.text.subtle"
        _hover={{ color: 'v2.text.primary' }}
      >
        <LinkExternal />
      </Icon>
    </TextTooltip>
  );

  return (
    <>
      {/* Staking Interest — same chrome as sibling cards; the accent lives in
          the label text only (team review #16: the accent left-border reads
          as template styling) */}
      <Flex {...railCardStyle}>
        <HStack
          justifyContent="space-between"
          alignItems="baseline"
          gap="8px"
          flexWrap="wrap"
          rowGap="2px"
        >
          <Text textStyle="v2.sectionLabel" color="v2.long.default">
            {t('lvmon.interest.title', 'Staking Interest')}
          </Text>
          <HStack gap="5px" alignItems="center">
            <Text textStyle="numbers.xsmall" color="v2.text.subtle">
              {t('lvmon.interest.meta', 'Monad POS rewards')}
            </Text>
            <TextTooltip
              content={
                <VStack alignItems="flex-start" gap={1}>
                  <Text>
                    LVMON staking is built atop Monad&apos;s native staking
                    architecture, ensuring a mechanism that is both secure and
                    robust.
                  </Text>
                  <Text>
                    Currently, Monad operates on a fixed cycle: every 50,000
                    blocks (the BOUNDARY_BLOCK_PERIOD) marks a boundary block.
                  </Text>
                  <Text>
                    These blocks, occurring roughly every 5.5 hours, serve to
                    commit pending staking updates and finalize the validator set
                    for the subsequent epoch.
                  </Text>
                </VStack>
              }
              triggerCss={{
                textDecoration: 'none',
                cursor: 'help',
              }}
              contentCss={{
                w: '320px',
                lineHeight: '18px',
                whiteSpace: 'normal',
              }}
            >
              <Text
                as="span"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="12px"
                h="12px"
                borderRadius="9999px"
                border="1px solid"
                borderColor="v2.border.strong"
                color="v2.text.subtle"
                fontSize={9}
                lineHeight="9px"
              >
                ?
              </Text>
            </TextTooltip>
          </HStack>
        </HStack>

        <Flex direction="column">
          <InfoRow isFirst label={t('lvmon.interest.lastDistribution', 'Last distribution')}>
            <Text
              textStyle="numbers.small"
              color="v2.text.primary"
              textAlign="right"
            >
              {formattedLastDistributionDisplay}
            </Text>
            {lastInterestDistribution?.transactionHash &&
              explorerIconLink(
                getExplorerLink(
                  lastInterestDistribution.transactionHash,
                  'transaction',
                  chainId,
                ),
                'View transaction',
              )}
          </InfoRow>

          <InfoRow label={t('lvmon.interest.nextDistribution', 'Next distribution')}>
            <Text textStyle="numbers.small" color="v2.text.primary" textAlign="right">
              {formattedNextInterestTime}
            </Text>
          </InfoRow>

          <InfoRow label={t('lvmon.interest.realtimeAmount', 'Realtime amount')}>
            <Text textStyle="numbers.small" color="v2.text.primary" textAlign="right">
              {formattedRealtimeInterestEstimateWithUnit}
            </Text>
            {treasuryVaultAddress &&
              explorerIconLink(
                getExplorerLink(treasuryVaultAddress, 'stake-portfolio', chainId),
                'View on explorer',
              )}
          </InfoRow>

          <InfoRow label={t('lvmon.interest.fullCycle', 'Est. full-cycle')}>
            <Text textStyle="numbers.small" color="v2.long.default" textAlign="right">
              {formattedFullCycleInterestEstimateWithUnit}
            </Text>
            {treasuryVaultAddress &&
              explorerIconLink(
                getExplorerLink(treasuryVaultAddress, 'stake-portfolio', chainId),
                'View on explorer',
              )}
          </InfoRow>
        </Flex>
      </Flex>

      {/* Interest-Bearing Assets */}
      <Flex {...railCardStyle}>
        <HStack justifyContent="space-between" alignItems="baseline" gap="8px">
          <Text textStyle="v2.sectionLabel" color="v2.text.subtle">
            {t('lvmon.assets.title', 'Interest-Bearing Assets')}
          </Text>
          <Text textStyle="numbers.xsmall" color="v2.text.subtle">
            {t('lvmon.assets.meta', 'via vault')}
          </Text>
        </HStack>

        <Flex direction="column">
          <AssetRow isFirst dotColor="v2.bg.surface3" name="gMON">
            <Text textStyle="numbers.small" color="v2.text.primary">
              -
            </Text>
          </AssetRow>
          <AssetRow dotColor="v2.bg.surface3" name="shMON">
            <Text textStyle="numbers.small" color="v2.text.primary">
              -
            </Text>
          </AssetRow>
          <AssetRow
            dotColor={
              stakedBalance && BigInt(stakedBalance) > 0n
                ? 'v2.long.default'
                : 'v2.bg.surface3'
            }
            name="dMON"
          >
            <Text textStyle="numbers.small" color="v2.text.primary">
              {formattedStakedBalance}
            </Text>
            {treasuryVaultAddress &&
              explorerIconLink(
                getExplorerLink(treasuryVaultAddress, 'stake-portfolio', chainId),
                'View on explorer',
              )}
          </AssetRow>
        </Flex>
      </Flex>
    </>
  );
};
